import React, { useEffect, useState } from "react";
import {
  Form,
  Select,
  DatePicker,
  Button,
  message,
} from "antd";
import styles from "./index.module.css";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  bookMedicalAssessment,
} from "../../store/medicalAssessmentSlice";
import { baseUrl } from "../../utils/api";
import axios from "axios";
import logo from "../../utils/img/loginLogo.png";
import {
  Stethoscope,
  MapPin,
  Building2,
  Calendar,
  Users,
  CheckCircle,
  Info,
  Copy,
  Printer,
  Phone,
  Mail,
  ArrowRight,
  FileText,
} from "lucide-react";

const { Option } = Select;

const MedicalAssessmentForm = () => {
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [savedDetails, setSavedDetails] = useState({});
  const [providers, setProviders] = useState([]);
  const [members, setMembers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [form] = Form.useForm();

  const { bookingStatus, bookingError, bookingData1 } = useSelector(
    (state) => state.medicalAssessment,
  );

  const getMembers = async () => {
    try {
      const accountId = sessionStorage.getItem("accountId") ?? 100010001016;
      const response = await axios.get(
        `${baseUrl}/member/client?clientAccountId=${accountId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setMembers(response?.data?.result);
      return response.data;
    } catch (error) {
      return;
    }
  };

  const getRegions = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/provider/profile/region-list`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setRegions(response?.data?.result);
      return response.data;
    } catch (error) {
      return;
    }
  };

  const searchProvider = async (query) => {
    try {
      const response = await axios.get(`${baseUrl}/provider/profile/search`, {
        params: { query },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setProviders(response?.data?.result ?? []);
      return response.data;
    } catch (error) {
      return;
    }
  };

  useEffect(() => {
    getRegions();
    getMembers();
  }, []);

  const handleLocationChange = async (value) => {
    setSelectedLocation(value);
    await searchProvider(value);
  };

  const handleSearch = (value) => {};

  const bookAppointment = async () => {
    const formValues = form.getFieldsValue();
    const bookingData = {
      member_account_id: formValues.member,
      location: selectedLocation,
      provider_account_id: formValues.facility,
      booking_date: moment(formValues.date).format("YYYY-MM-DD"),
    };
    await dispatch(bookMedicalAssessment({ bookingData, setStep }));
  };

  useEffect(() => {
    if (bookingData1) {
      setSavedDetails({
        key: "1",
        facility: bookingData1?.provider_name,
        bookingCode: bookingData1?.booking_id,
        appointmentDate: bookingData1?.booking_date,
        location: bookingData1?.location,
        address: bookingData1?.provider_address,
      });
    }
  }, [bookingData1]);

  const copyBookingCode = () => {
    navigator.clipboard.writeText(savedDetails.bookingCode);
    message.success("Booking code copied to clipboard");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Appointment Confirmation - ${bookingData1?.member_name}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #334155; }
            h1 { color: #028D86; font-size: 24px; margin-bottom: 8px; }
            .subtitle { color: #64748b; margin-bottom: 32px; }
            .section { border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
            .section h2 { color: #028D86; font-size: 16px; margin: 0 0 16px; }
            .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
            .row:last-child { border-bottom: none; }
            .label { color: #64748b; font-size: 14px; }
            .value { font-weight: 600; font-size: 14px; }
            .notice { background: #f0faf9; border: 1px solid #d1edeb; border-radius: 12px; padding: 16px 20px; margin-bottom: 20px; font-size: 14px; line-height: 1.6; }
            .footer { text-align: center; margin-top: 40px; color: #94a3b8; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>Appointment Confirmation</h1>
          <p class="subtitle">Generated on ${new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>

          <div class="section">
            <h2>Patient Information</h2>
            <div class="row"><span class="label">Name</span><span class="value">${bookingData1?.member_name}</span></div>
            <div class="row"><span class="label">Booking Code</span><span class="value">${bookingData1?.booking_id}</span></div>
          </div>

          <div class="section">
            <h2>Appointment Details</h2>
            <div class="row"><span class="label">Date</span><span class="value">${savedDetails.appointmentDate}</span></div>
            <div class="row"><span class="label">Provider</span><span class="value">${bookingData1?.provider_name}</span></div>
            <div class="row"><span class="label">Location</span><span class="value">${savedDetails.location || "Not specified"}</span></div>
            <div class="row"><span class="label">Address</span><span class="value">${savedDetails.address || "Not specified"}</span></div>
          </div>

          <div class="notice">
            <strong>Important:</strong> The assessment must be completed within <strong>7 days</strong> of booking. After this period, a new appointment will need to be booked. Present your booking code to the provider for identity verification.
          </div>

          <div class="footer">
            <p>For assistance, contact support@edencaremedical.com</p>
          </div>
          <script>window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.brandMark}>
            <img src={logo} alt="Eden Care" className={styles.brandLogo} />
          </div>
          <div className={styles.topBarRight}>
            <span className={styles.topBarBadge}>Medical Assessment</span>
          </div>
        </div>
      </div>

      <div className={styles.main}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h1 className={styles.headingMain}>
              {step === 1 ? "Book Medical Assessment" : "Assessment Booked"}
            </h1>
            <p className={styles.subHeading}>
              To ensure we provide you with the best coverage, we need to conduct a medical assessment.
            </p>
          </div>

          {/* Progress indicator */}
          <div className={styles.stepper}>
            <div className={`${styles.stepItem} ${step >= 1 ? styles.stepActive : ""}`}>
              <div className={styles.stepCircle}>
                {step > 1 ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (
                  <Stethoscope size={18} />
                )}
              </div>
              <div className={styles.stepLabel}>
                <span className={styles.stepNumber}>Step 1</span>
                <span className={styles.stepTitle}>Book Appointment</span>
              </div>
            </div>
            <div className={`${styles.stepConnector} ${step > 1 ? styles.stepConnectorDone : ""}`} />
            <div className={`${styles.stepItem} ${step >= 2 ? styles.stepActive : ""}`}>
              <div className={styles.stepCircle}>
                <CheckCircle size={18} />
              </div>
              <div className={styles.stepLabel}>
                <span className={styles.stepNumber}>Step 2</span>
                <span className={styles.stepTitle}>Confirmation</span>
              </div>
            </div>
          </div>

          {step === 1 ? (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardIcon}>
                  <Stethoscope size={22} />
                </div>
                <div>
                  <h3 className={styles.cardTitle}>Appointment Details</h3>
                  <span className={styles.cardDesc}>Select the member, location, provider and date for the assessment</span>
                </div>
              </div>

              <div className={styles.cardBody}>
                <Form layout="vertical" form={form}>
                  {/* Member section */}
                  <div className={styles.sectionLabel}>
                    <Users size={15} color="#028D86" />
                    <span>Person going for Medical Examination</span>
                  </div>
                  <Form.Item
                    label={<span className={styles.fieldLabel}><Users size={16} color="#028D86" /> Member</span>}
                    required
                    name="member"
                    className={styles.formItem}
                  >
                    <Select
                      className={styles.fieldSelect}
                      placeholder="Select Member"
                      onChange={handleSearch}
                      showSearch
                      optionFilterProp="children"
                    >
                      {members.map((member) => (
                        <Option key={member.member_account_id} value={member.member_account_id}>
                          {member.member_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {/* Location & Provider */}
                  <div className={styles.sectionLabel}>
                    <MapPin size={15} color="#028D86" />
                    <span>Select from our Provider Panel below the center you would like to visit</span>
                  </div>
                  <div className={styles.formGrid}>
                    <Form.Item
                      label={<span className={styles.fieldLabel}><MapPin size={16} color="#028D86" /> Location</span>}
                      required
                      name="location"
                      className={styles.formItem}
                    >
                      <Select
                        placeholder="Select Location"
                        className={styles.fieldSelect}
                        onChange={handleLocationChange}
                        showSearch
                        optionFilterProp="children"
                      >
                        {regions.map((r, i) => (
                          <Option key={i} value={r}>{r}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label={<span className={styles.fieldLabel}><Building2 size={16} color="#028D86" /> Provider</span>}
                      required
                      name="facility"
                      className={styles.formItem}
                    >
                      <Select
                        className={styles.fieldSelect}
                        placeholder="Select Provider"
                        showSearch
                        optionFilterProp="children"
                      >
                        {providers && providers.length > 0 && providers.map((provider) => (
                          <Option key={provider.provider_account_id} value={provider.provider_account_id}>
                            {provider.provider_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  {/* Date */}
                  <div className={styles.sectionLabel}>
                    <Calendar size={15} color="#028D86" />
                    <span>Select the date you would like to visit</span>
                  </div>
                  <Form.Item
                    label={<span className={styles.fieldLabel}><Calendar size={16} color="#028D86" /> Appointment Date</span>}
                    required
                    name="date"
                    className={styles.formItem}
                  >
                    <DatePicker
                      className={styles.fieldDatePicker}
                      placeholder="Select date"
                      disabledDate={(current) => {
                        const isBefore = current && current < moment().startOf("day");
                        const isAfter15Days = current && current > moment().add(15, "days").endOf("day");
                        return isBefore || isAfter15Days;
                      }}
                    />
                  </Form.Item>
                </Form>

                {bookingError && (
                  <div className={styles.errorBanner}>
                    <Info size={16} />
                    <span>{bookingError}</span>
                  </div>
                )}

                <div className={styles.actions}>
                  <Button className={styles.cancelBtn}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    className={styles.submitBtn}
                    onClick={bookAppointment}
                    loading={bookingStatus === "loading"}
                  >
                    Book Appointment
                    <ArrowRight size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* ===== STEP 2: Confirmation ===== */
            <>
              {/* Success banner */}
              <div className={styles.successBanner}>
                <div className={styles.successIcon}>
                  <CheckCircle size={28} />
                </div>
                <div>
                  <h3 className={styles.successTitle}>
                    Appointment for <strong>{bookingData1?.member_name}</strong> has been successfully booked
                  </h3>
                  <span className={styles.successDate}>
                    {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                  </span>
                </div>
              </div>

              {/* Info notice */}
              <div className={styles.noticeBanner}>
                <div className={styles.noticeIcon}>
                  <Info size={18} />
                </div>
                <div className={styles.noticeContent}>
                  <p>
                    The assessment must be completed within <strong>7 days</strong> of booking. After this period, you will need to book a new appointment.
                  </p>
                  <p>
                    The booking code provided will help the service provider verify the identity of the member.
                  </p>
                </div>
              </div>

              {/* Booking details card */}
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <FileText size={22} />
                  </div>
                  <div>
                    <h3 className={styles.cardTitle}>Booking Details</h3>
                    <span className={styles.cardDesc}>Your appointment confirmation summary</span>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.detailsGrid}>
                    <div className={styles.detailCard}>
                      <div className={styles.detailIcon}>
                        <Calendar size={20} />
                      </div>
                      <span className={styles.detailLabel}>Appointment Date</span>
                      <span className={styles.detailValue}>{savedDetails.appointmentDate}</span>
                    </div>
                    <div className={styles.detailCard}>
                      <div className={styles.detailIcon}>
                        <Building2 size={20} />
                      </div>
                      <span className={styles.detailLabel}>Provider</span>
                      <span className={styles.detailValue}>{bookingData1?.provider_name}</span>
                    </div>
                    <div className={styles.detailCard}>
                      <div className={styles.detailIcon}>
                        <MapPin size={20} />
                      </div>
                      <span className={styles.detailLabel}>Location</span>
                      <span className={styles.detailValue}>{savedDetails.location || "—"}</span>
                    </div>
                    <div className={styles.detailCard}>
                      <div className={styles.detailIcon}>
                        <Stethoscope size={20} />
                      </div>
                      <span className={styles.detailLabel}>Address</span>
                      <span className={styles.detailValue}>{savedDetails.address || "—"}</span>
                    </div>
                  </div>

                  {/* Booking code highlight */}
                  <div className={styles.bookingCodeWrap}>
                    <div className={styles.bookingCodeInfo}>
                      <span className={styles.bookingCodeLabel}>Booking Code</span>
                      <span className={styles.bookingCodeValue}>{savedDetails.bookingCode}</span>
                    </div>
                    <button type="button" className={styles.copyBtn} onClick={copyBookingCode}>
                      <Copy size={16} />
                      Copy
                    </button>
                  </div>

                  <div className={styles.actions} style={{ justifyContent: "center" }}>
                    <Button
                      type="primary"
                      className={styles.submitBtn}
                      onClick={handlePrint}
                    >
                      <Printer size={16} />
                      Print Details
                    </Button>
                  </div>
                </div>
              </div>

              {/* Support footer */}
              <div className={styles.supportBanner}>
                <div className={styles.supportIcon}>
                  <Phone size={18} />
                </div>
                <div>
                  <span className={styles.supportTitle}>Need help with your appointment?</span>
                  <span className={styles.supportDesc}>
                    Contact our support team at{" "}
                    <a href="mailto:support@edencaremedical.com" className={styles.supportLink}>
                      <Mail size={14} /> support@edencaremedical.com
                    </a>
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalAssessmentForm;
